import { useUnifiedChatStore } from '@/store/app/unifiedChat'
import type { UnifiedChatActiveTarget } from '@/store/app/unifiedChat/types'
import type { AIChatSession, AIChatSessionMessage, Document } from '@/types/generated'
import { WebSocketMessageType } from '@/utils/channels/types'
import {
  groupAttachesByTargetMessage,
  parseTimestamp,
  type ContextAttachEvent
} from '@/utils/chatContextGrouping'
import { computed, onUnmounted, ref, watch } from 'vue'

/** Metadata for a context annotation shown in the chat thread. */
export interface ChatContextData {
  /** Case label (e.g. "S. Hofmann ./. H. Schmidt"). */
  caseLabel?: string
  /** Case UUID for reference. */
  caseUuid?: string
  /** Documents attached to the session at the time of context addition. */
  documents?: Array<{ filename: string; uuid: string }>
}

/**
 * Lightweight message shape used inside the streaming composable.
 *
 * Intentionally decoupled from the generated `AIChatSessionMessage` to keep the
 * composable agnostic of backend schema changes and to support the `_isStreaming`
 * flag that only lives on the client.
 */
export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system' | 'context'
  content: string
  /** Structured chunks for rich content (verdicts, entities, documents). Null for plain text. */
  chunks: any[] | null
  isStreaming: boolean
  createdAt: string
  /** Original backend message (set after AI_CHAT_MESSAGE_COMPLETE replaces the placeholder). */
  raw?: AIChatSessionMessage
  /** Present when role === 'context'. Holds case & document metadata for the annotation. */
  contextData?: ChatContextData
}

export type StreamingStatus = 'idle' | 'connecting' | 'streaming' | 'complete' | 'error'

function generateId(): string {
  return crypto.randomUUID()
}

/**
 * Extract plain text from an AIChatSessionMessage's `content` field.
 *
 * The backend may return content as either:
 * - A plain string (simple responses)
 * - A structured object `{ chunks: Array<{ type: string; text?: string; ... }> }` (rich responses
 *   with verdicts, entity links, etc.)
 *
 * This function normalises both to a single string so message renderers always have text to show.
 */
function extractTextContent(content: unknown): string {
  if (typeof content === 'string') return content
  if (content && typeof content === 'object' && 'chunks' in content) {
    const chunks = (content as { chunks: any[] }).chunks
    if (Array.isArray(chunks)) {
      return chunks
        .filter((c) => c.type === 'text')
        .map((c) => c.text ?? '')
        .join('\n\n')
    }
  }
  return ''
}

/**
 * Extract structured chunks from an AIChatSessionMessage's `content` field, if present.
 */
function extractChunks(content: unknown): any[] | null {
  if (content && typeof content === 'object' && 'chunks' in content) {
    const chunks = (content as { chunks: any[] }).chunks
    return Array.isArray(chunks) ? chunks : null
  }
  return null
}

// ── Context annotation persistence ──────────────────────────────────
//
// The session API now returns case_uuid and case_title. sessionStorage
// persistence is a fallback for the current browser session — it auto-clears
// on browser close so sensitive case/document metadata isn't retained long-term.

const CONTEXT_STORAGE_PREFIX = 'chat-context-'

/**
 * Persist a context payload (case label, etc.) for the given session.
 *
 * Multiple banners can be injected in one session — each typically carrying
 * just the new delta for that turn — so the persisted entry **merges** the
 * incoming fields with whatever is already stored. That way the case label
 * from the very first banner survives later doc-only banners that omit it,
 * keeping the fallback in :func:`buildContextMessages` reliable on reload.
 */
export function persistContextData(sessionUuid: string, data: ChatContextData): void {
  try {
    const existing = loadPersistedContextData(sessionUuid)
    const merged: ChatContextData = {
      caseLabel: data.caseLabel ?? existing?.caseLabel,
      caseUuid: data.caseUuid ?? existing?.caseUuid,
      documents: data.documents ?? existing?.documents
    }
    sessionStorage.setItem(`${CONTEXT_STORAGE_PREFIX}${sessionUuid}`, JSON.stringify(merged))
  } catch {
    // Storage full or unavailable — non-critical
  }
}

export function loadPersistedContextData(sessionUuid: string): ChatContextData | null {
  try {
    const raw = sessionStorage.getItem(`${CONTEXT_STORAGE_PREFIX}${sessionUuid}`)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    return parsed as ChatContextData
  } catch {
    return null
  }
}

export function clearPersistedContextData(sessionUuid: string): void {
  try {
    sessionStorage.removeItem(`${CONTEXT_STORAGE_PREFIX}${sessionUuid}`)
  } catch {
    // ignore
  }
}

/**
 * Synthesize one context banner per attach turn from a session's persisted
 * state.
 *
 * Each ``session.documents[i]`` records its own ``created_at`` and the case
 * (if any) records ``case_linked_at`` — those timestamps are mapped to the
 * "target message" they preceded so docs uploaded across multiple turns land
 * in their own banners above their own messages, instead of all collapsing
 * into a single banner above the earliest message.
 *
 * Falls back to ``sessionStorage`` for the case label only when the API hasn't
 * surfaced ``case_title`` yet (transitional rollout window).
 *
 * :param session: Loaded session payload, including ``messages``,
 *     ``documents`` and the case-link metadata.
 * :param chatMessages: The same messages already mapped to :class:`ChatMessage`
 *     in thread order. Used both for the target-message lookup and to ensure
 *     the rendered ``insertIdx`` is consistent with the array we will splice
 *     the banners into.
 * :return: List of ``{ insertIdx, message }`` pairs in ascending ``insertIdx``
 *     order. Empty when there is nothing to render.
 */
function buildContextMessages(
  session: AIChatSession,
  chatMessages: ChatMessage[]
): Array<{ insertIdx: number; message: ChatMessage }> {
  const liveDocs = (session.documents ?? []).filter((d: Document) => !d.is_deleted)
  const hasCaseLinked = !!session.case_linked_at

  if (!hasCaseLinked && liveDocs.length === 0) return []

  let caseLabel = session.case_title ?? undefined
  let caseUuid = session.case_uuid ?? undefined
  if (caseLabel) {
    clearPersistedContextData(session.uuid)
  } else if (hasCaseLinked) {
    const persisted = loadPersistedContextData(session.uuid)
    if (persisted) {
      caseLabel = persisted.caseLabel
      caseUuid = caseUuid ?? persisted.caseUuid
    }
  }

  const events: ContextAttachEvent[] = []
  for (const d of liveDocs) {
    const epoch = parseTimestamp(d.created_at)
    if (epoch === null) continue
    events.push({
      kind: 'doc',
      epoch,
      doc: { uuid: d.uuid, filename: d.filename || d.title || '' }
    })
  }
  if (hasCaseLinked && caseLabel) {
    const epoch = parseTimestamp(session.case_linked_at as string | null | undefined)
    if (epoch !== null) {
      events.push({ kind: 'case', epoch, caseLabel, caseUuid })
    }
  }

  if (events.length === 0) {
    // Timestamps unavailable (very old sessions). Fall back to a single banner
    // at the top so the user still sees their attached context.
    return [
      {
        insertIdx: 0,
        message: {
          id: `context-${session.uuid}`,
          role: 'context',
          content: '',
          chunks: null,
          isStreaming: false,
          createdAt: '',
          contextData: {
            caseLabel: caseLabel || undefined,
            caseUuid: caseUuid || undefined,
            documents: liveDocs.map((d: Document) => ({
              filename: d.filename || d.title || '',
              uuid: d.uuid
            }))
          }
        }
      }
    ]
  }

  const msgSlots = chatMessages.map((m) => ({
    epoch: parseTimestamp(m.createdAt),
    targetable: m.role === 'user'
  }))
  const groups = groupAttachesByTargetMessage(msgSlots, events)

  return groups.map((g, i) => ({
    insertIdx: g.insertIdx,
    message: {
      id: `context-${session.uuid}-${i}`,
      role: 'context' as const,
      content: '',
      chunks: null,
      isStreaming: false,
      createdAt: '',
      contextData: {
        caseLabel: g.caseLabel,
        caseUuid: g.caseUuid,
        documents: g.documents
      }
    }
  }))
}

/**
 * Reusable composable for AI chat streaming — works for both side panel and fullscreen targets.
 *
 * Owns: messages array, streaming accumulators, send flow, WebSocket event handling.
 * Delegates to: `useUnifiedChatStore` for session CRUD and WebSocket plumbing.
 *
 * Does NOT own: UI rendering, scroll behavior, prompt templates, document processing.
 */
export function useChatStream(
  target: UnifiedChatActiveTarget = 'sidepanel',
  options?: {
    courtDbEnabled?: import('vue').Ref<boolean>
    model?: import('vue').Ref<string | null>
  }
) {
  const unifiedChat = useUnifiedChatStore()

  const messages = ref<ChatMessage[]>([])
  const streamingStatus = ref<StreamingStatus>('idle')
  const streamingText = ref('')
  const streamingChunks = ref<any[]>([])
  const streamError = ref<string | null>(null)
  const sessionState = ref<string | null>(null)

  let placeholderPushed = false
  let currentLoadId = 0
  let sendLockSessionUuid: string | null = null

  const isStreaming = computed(
    () => streamingStatus.value === 'connecting' || streamingStatus.value === 'streaming'
  )
  const hasMessages = computed(() => messages.value.length > 0)

  const activeSession = computed(() =>
    target === 'sidepanel'
      ? unifiedChat.activeSidePanelSession
      : unifiedChat.activeFullscreenSession
  )

  const ws = computed(() => unifiedChat.getSessionWebSocket(target))

  // ── Content events that should trigger placeholder creation ────────
  const contentEvents = new Set<string>([
    WebSocketMessageType.AI_CHAT_TEXT_DELTA,
    WebSocketMessageType.AI_CHAT_TEXT_CHUNK_END,
    WebSocketMessageType.AI_CHAT_CHUNK,
    WebSocketMessageType.AI_CHAT_MESSAGE_COMPLETE
  ])

  // ── WebSocket message handling ─────────────────────────────────────

  // Sync flush: the default 'pre' scheduler coalesces rapid WS events into a
  // single watcher call with the latest value, which silently drops
  // message_complete (the only event that finalises the streaming placeholder)
  // when the browser delivers several frames in the same macrotask.
  watch(
    () => ws.value.lastMessage.value,
    (message) => {
      if (!message) return
      if (!activeSession.value) return

      // If somehow we get a content event without a placeholder (e.g. external broadcast),
      // push one as a fallback.
      if (!placeholderPushed && contentEvents.has(message.type)) {
        messages.value.push({
          id: generateId(),
          role: 'assistant',
          content: '',
          chunks: [],
          isStreaming: true,
          createdAt: new Date().toISOString()
        })
        placeholderPushed = true
      }

      switch (message.type) {
        case WebSocketMessageType.AI_CHAT_SESSION_STATE_CHANGE:
          sessionState.value = message.data.state ?? null
          break

        case WebSocketMessageType.AI_CHAT_TEXT_DELTA:
          streamingText.value += message.data.delta
          updateStreamingMessage()
          break

        case WebSocketMessageType.AI_CHAT_TEXT_CHUNK_END:
          if (streamingText.value) {
            streamingChunks.value.push({ type: 'text', text: streamingText.value })
            streamingText.value = ''
          }
          break

        case WebSocketMessageType.AI_CHAT_CHUNK:
          streamingChunks.value.push(message.data)
          break

        case WebSocketMessageType.AI_CHAT_MESSAGE_COMPLETE: {
          const completeMessage = message.data.message as AIChatSessionMessage
          const idx = messages.value.findIndex((m) => m.isStreaming)
          const finalized: ChatMessage = {
            id: completeMessage.uuid ?? generateId(),
            role: (completeMessage.role as 'user' | 'assistant' | 'system') ?? 'assistant',
            content: extractTextContent(completeMessage.content),
            chunks: extractChunks(completeMessage.content),
            isStreaming: false,
            createdAt: completeMessage.created_at ?? new Date().toISOString(),
            raw: completeMessage
          }
          if (idx !== -1) {
            messages.value[idx] = finalized
          } else if (
            messages.value.length > 0 &&
            messages.value[messages.value.length - 1].role === 'assistant'
          ) {
            messages.value[messages.value.length - 1] = finalized
          } else {
            messages.value.push(finalized)
          }
          break
        }

        case WebSocketMessageType.AI_CHAT_DONE:
          cleanupStreaming()
          break

        case WebSocketMessageType.AI_CHAT_ERROR: {
          const errorMsg = message.data.message ?? 'An error occurred'
          replaceStreamingPlaceholderWithError(errorMsg)
          streamError.value = errorMsg
          cleanupStreaming()
          streamingStatus.value = 'error'
          break
        }

        case WebSocketMessageType.AI_CHAT_MODEL_RESOLVED:
          // Model resolved — could update UI to show which model is active
          break

        case WebSocketMessageType.AI_CHAT_MODEL_ERROR: {
          const errorMsg = message.data.error ?? 'Selected model is not available'
          replaceStreamingPlaceholderWithError(errorMsg)
          streamError.value = errorMsg
          cleanupStreaming()
          streamingStatus.value = 'error'
          break
        }
      }
    },
    { flush: 'sync' }
  )

  function updateStreamingMessage() {
    const idx = messages.value.findIndex((m) => m.isStreaming)
    if (idx !== -1) {
      // Build content from completed chunks + live streaming text.
      // Chunks are separated by double-newlines so markdown block elements (tables, lists)
      // that span a chunk boundary still parse correctly.
      const completedParts = streamingChunks.value
        .filter((c) => c.type === 'text')
        .map((c) => c.text)
      const allParts = streamingText.value
        ? [...completedParts, streamingText.value]
        : completedParts
      messages.value[idx] = {
        ...messages.value[idx],
        content: allParts.join('\n\n')
      }
    }
  }

  function replaceStreamingPlaceholderWithError(errorMsg: string) {
    const idx = messages.value.findIndex((m) => m.isStreaming)
    if (idx !== -1) {
      messages.value[idx] = {
        ...messages.value[idx],
        content: errorMsg,
        isStreaming: false
      }
    } else {
      // Error arrived before any content event pushed a placeholder — e.g.
      // Bedrock AccessDenied throws immediately. Push a fresh assistant
      // bubble so the user sees the failure instead of nothing.
      messages.value.push({
        id: generateId(),
        role: 'assistant',
        content: errorMsg,
        chunks: null,
        isStreaming: false,
        createdAt: new Date().toISOString()
      })
    }
  }

  function cleanupStreaming() {
    // Keep the WS connection alive — disconnecting on every DONE causes a
    // race condition where the next sendMessage fires before reconnect
    // completes, so response events are lost (appeared only on page reload).
    streamingText.value = ''
    streamingChunks.value = []
    placeholderPushed = false
    sessionState.value = null
    if (streamingStatus.value !== 'error') {
      streamingStatus.value = 'idle'
    }
  }

  // ── Send message ───────────────────────────────────────────────────

  function cancelStreamingForRouteSwitch(): void {
    sendLockSessionUuid = null
    streamError.value = null
    if (streamingStatus.value !== 'idle' && streamingStatus.value !== 'complete') {
      cleanupStreaming()
    }
    streamingStatus.value = 'idle'
  }

  async function sendMessage(content: string): Promise<void> {
    const trimmed = content.trim()
    if (!trimmed) return

    if (target === 'sidepanel') {
      unifiedChat.setSidePanelPreferEmptySession(false)
    }

    // Clean up any in-flight stream
    if (isStreaming.value) {
      cleanupStreaming()
    }

    // Create session if none exists
    let session = activeSession.value
    if (!session) {
      try {
        session = await unifiedChat.createSession(
          options?.courtDbEnabled?.value ? { courtDbEnabled: true } : undefined,
          target
        )
      } catch {
        streamError.value = 'Failed to create chat session'
        streamingStatus.value = 'error'
        return
      }
    }

    if (!session) {
      streamError.value = 'No active session'
      streamingStatus.value = 'error'
      return
    }

    // Lock to this session so concurrent clearChat/loadSession can detect a stale send
    const lockedSessionUuid = session.uuid!
    sendLockSessionUuid = lockedSessionUuid

    // Update title if it's still the default and this is the first user message
    const userMessageCount = messages.value.filter((m) => m.role === 'user').length
    if (userMessageCount === 0 && (!session.title || session.title === 'New Chat')) {
      const newTitle = trimmed.length > 50 ? trimmed.substring(0, 50) + '...' : trimmed
      try {
        await unifiedChat.updateSession(session.uuid!, { title: newTitle })
      } catch {
        // Non-critical — continue sending
      }
      // Check if session changed while we awaited
      if (sendLockSessionUuid !== lockedSessionUuid) {
        cleanupStreaming()
        return
      }
    }

    // Push user message locally
    messages.value.push({
      id: generateId(),
      role: 'user',
      content: trimmed,
      chunks: null,
      isStreaming: false,
      createdAt: new Date().toISOString()
    })

    // Reset streaming state and push assistant placeholder immediately so the
    // loader is visible while we connect the WebSocket and wait for the first token.
    streamingText.value = ''
    streamingChunks.value = []
    streamError.value = null
    placeholderPushed = true
    streamingStatus.value = 'connecting'

    messages.value.push({
      id: generateId(),
      role: 'assistant',
      content: '',
      chunks: [],
      isStreaming: true,
      createdAt: new Date().toISOString()
    })

    // Connect WebSocket and send
    try {
      await unifiedChat.connectSessionWebSocket(target)
      // Check if session changed while we awaited the WS connection
      if (sendLockSessionUuid !== lockedSessionUuid) {
        // Roll back the user message and streaming placeholder pushed during this send
        const placeholderIdx = messages.value.findIndex((m) => m.isStreaming)
        if (placeholderIdx !== -1) {
          messages.value.splice(placeholderIdx, 1)
        }
        // Remove the user message we just pushed (last 'user' row)
        for (let i = messages.value.length - 1; i >= 0; i--) {
          if (messages.value[i].role === 'user' && messages.value[i].content === trimmed) {
            messages.value.splice(i, 1)
            break
          }
        }
        cleanupStreaming()
        return
      }
      ws.value.sendMessage({
        type: 'send_message',
        data: {
          content: trimmed,
          court_database_flag_enabled: options?.courtDbEnabled?.value ?? false,
          model: options?.model?.value ?? null
        }
      })
      streamingStatus.value = 'streaming'
    } catch {
      // Remove the placeholder and show error
      const placeholderIdx = messages.value.findIndex((m) => m.isStreaming)
      if (placeholderIdx !== -1) {
        messages.value.splice(placeholderIdx, 1)
      }
      streamError.value = 'Failed to connect to chat'
      streamingStatus.value = 'error'
      placeholderPushed = false
    } finally {
      if (sendLockSessionUuid === lockedSessionUuid) {
        sendLockSessionUuid = null
      }
    }
  }

  // ── Load existing session messages ─────────────────────────────────

  /**
   * Load an existing session from the API into local message state.
   *
   * :param sessionUuid: Backend session id
   * :return: True when messages were applied; false when superseded, failed, or missing data
   */
  async function loadSession(sessionUuid: string): Promise<boolean> {
    // Stop any in-flight stream before switching sessions so old WebSocket
    // events don't bleed into the newly loaded session.
    if (isStreaming.value) {
      cleanupStreaming()
    }
    streamError.value = null
    sendLockSessionUuid = null

    const loadId = ++currentLoadId

    try {
      const session = await unifiedChat.loadSession(sessionUuid, target)
      // Guard: ignore if a newer load was started while we awaited
      if (loadId !== currentLoadId) {
        return false
      }
      const rawMessages = (session?.messages as AIChatSessionMessage[] | undefined) ?? []
      const chatMessages: ChatMessage[] = rawMessages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({
          id: m.uuid ?? generateId(),
          role: m.role as 'user' | 'assistant',
          content: extractTextContent(m.content),
          chunks: extractChunks(m.content),
          isStreaming: false,
          createdAt: m.created_at ?? '',
          raw: m
        }))

      // Synthesize one context banner per attach turn so docs uploaded across
      // multiple turns land above their respective user messages instead of
      // all collapsing into a single banner above the earliest message.
      // See :func:`buildContextMessages` for the grouping rule.
      if (session) {
        const contextMsgs = buildContextMessages(session, chatMessages)
        // Splice from the largest index down so earlier inserts don't shift
        // indices that haven't been applied yet.
        for (let i = contextMsgs.length - 1; i >= 0; i--) {
          const { insertIdx, message } = contextMsgs[i]
          if (insertIdx >= chatMessages.length) {
            chatMessages.push(message)
          } else {
            chatMessages.splice(insertIdx, 0, message)
          }
        }
      }

      messages.value = chatMessages
      return true
    } catch {
      if (loadId !== currentLoadId) {
        return false
      }
      streamError.value = 'Failed to load chat session'
      return false
    }
  }

  // ── Retry ──────────────────────────────────────────────────────────

  function retryLastMessage(): void {
    const lastUserMsg = [...messages.value].reverse().find((m) => m.role === 'user')
    if (!lastUserMsg) return

    // Remove trailing assistant messages (failed/partial) back to the last user message
    while (messages.value.length > 0) {
      const tail = messages.value[messages.value.length - 1]
      if (tail.role === 'assistant') {
        messages.value.pop()
      } else {
        break
      }
    }
    // Remove the user message too — sendMessage will re-add it
    if (messages.value.length > 0 && messages.value[messages.value.length - 1].role === 'user') {
      messages.value.pop()
    }

    streamError.value = null
    streamingStatus.value = 'idle'
    sendMessage(lastUserMsg.content)
  }

  // ── New chat ───────────────────────────────────────────────────────

  function clearChat(): void {
    if (isStreaming.value) {
      cleanupStreaming()
    }
    const sessionUuid = activeSession.value?.uuid
    if (sessionUuid) clearPersistedContextData(sessionUuid)
    sendLockSessionUuid = null
    messages.value = []
    streamError.value = null
    streamingStatus.value = 'idle'
    if (target === 'sidepanel') {
      unifiedChat.clearSidePanelSessionSnapshot()
    }
    unifiedChat.setActiveSession(null, target)
  }

  // ── Context annotation ─────────────────────────────────────────────

  /**
   * Inject a context banner at the current position in the chat.
   *
   * Each call appends a **new** banner at the end of the message list — i.e.
   * just above the next user message about to be sent. Earlier banners are
   * preserved in place so a doc/case attached for a later turn shows up
   * adjacent to that turn's message rather than coalescing into the original
   * banner above an older message (the previous in-place-update behavior).
   *
   * Callers are responsible for passing only the **delta** for this turn
   * (case label only on the turn it was first attached; doc list only with
   * docs not already shown in an earlier banner) — the composable itself does
   * not deduplicate against prior banners.
   *
   * :param data: Context payload (case + new documents) for this turn.
   * :return: None.
   */
  function injectContextMessage(data: ChatContextData): void {
    const sessionUuid = activeSession.value?.uuid
    if (sessionUuid) {
      persistContextData(sessionUuid, data)
    }

    messages.value.push({
      id: `context-${generateId()}`,
      role: 'context',
      content: '',
      chunks: null,
      isStreaming: false,
      createdAt: new Date().toISOString(),
      contextData: data
    })
  }

  // ── Lifecycle ──────────────────────────────────────────────────────

  onUnmounted(() => {
    if (isStreaming.value) {
      cleanupStreaming()
    }
  })

  return {
    // State
    messages,
    streamingStatus,
    streamingText,
    streamingChunks,
    streamError,
    sessionState,
    activeSession,

    // Computed
    isStreaming,
    hasMessages,

    // Actions
    sendMessage,
    retryLastMessage,
    loadSession,
    clearChat,
    cancelStreamingForRouteSwitch,
    injectContextMessage
  }
}
